import {
  PlusIcon as AddToShoppingListIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/src/components/ui/Button";
import Spinner from "@/components/Spinner";
import TooltipPopover from "@/components/TooltipPopover";
import useGTM from "@/hooks/useGTM";
import { useStores } from "@/hooks/useStores";
import usePaywall from "@/hooks/usePaywall";
import useSubscriptionPermission from "@/hooks/useSubscriptionPermission";
import { getEventDataFromRecipe } from "@/src/recipes/lib/getEventDataFromRecipe";
import clsx from "clsx";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import posthog from "posthog-js";
import { useFeatureFlagVariantKey } from "posthog-js/react";
import { useMemo, useState } from "react";
import { useShoppingListContext } from "@/contexts/ShoppingListContext";
import { usePlannerContext } from "@/contexts/PlannerContext";

// ----------------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------------

type Recipe = {
  id: number;
  [key: string]: unknown;
};

type Variant = "icon" | "button";

interface SaveRecipeToShoppingListProps {
  recipe: Recipe;
  /**
   * Determines the visual treatment of the component.
   * "icon"  – square icon-only button (used on thumbnails)
   * "button" – pill-shaped (standard) button with text (used on recipe pages)
   */
  variant?: Variant;
  /**
   * Where the recipe was added from – forwarded to analytics payloads.
   * Defaults to "thumbnail" for the icon variant and "recipe page" for the button variant.
   */
  recipeAddedFrom?: string;
  className?: string;
}

const SaveRecipeToShoppingList = ({
  recipe,
  variant = "icon",
  recipeAddedFrom = variant === "icon" ? "thumbnail" : "recipe page",
  className,
}: SaveRecipeToShoppingListProps) => {
  const [isLoading, setIsLoading] = useState(false);

  // Use new shopping list context
  const {
    addRecipeToShoppingList,
    isRecipeInShoppingList,
    canAddToShoppingList,
    getDefaultShoppingList,
  } = useShoppingListContext();

  // Keep planner context for the experiment flags and modal state
  const { setIsShoppingListOpen, shouldAllowFreeUsersShoppingList } =
    usePlannerContext();

  const { authStore } = useStores();
  const currentUser = authStore.currentUser;
  const { setPaywall } = usePaywall();
  const { subscriptionPermission } = useSubscriptionPermission(recipe);
  const { canUsePlanner } = subscriptionPermission;
  const gtm = useGTM();

  // Get experiment variant for tracking
  const experimentVariant = useFeatureFlagVariantKey(
    "cro-web-147-shopping-list-to-free-users-paywall"
  );

  // Check if recipe is in shopping list
  const shoppingList = getDefaultShoppingList();

  const recipeInShoppingList = useMemo(() => {
    return isRecipeInShoppingList(recipe.id, shoppingList?.id);
  }, [isRecipeInShoppingList, recipe.id, shoppingList?.id]);

  const handleAddToShoppingList = debounce(
    async () => {
      const isFreePlan = currentUser?.plan === 3;
      const isPremiumPlan = currentUser?.plan === 2;

      // Determine paywall variant based on experiment and user state
      const getPaywallVariant = () => {
        if (shouldAllowFreeUsersShoppingList && isFreePlan) {
          // For free users in treatment variants who hit the 2-recipe limit
          return "addMoreToShoppingList";
        }
        // For control users or non-free users who can't access shopping list
        return "addToShoppingList";
      };

      // Control vs Variant logic based on experiment
      if (shouldAllowFreeUsersShoppingList) {
        // Variant: Allow both free (plan 3) and premium (plan 2) users to use shopping list
        if (!canUsePlanner && !isFreePlan && !isPremiumPlan) {
          setPaywall({
            isOpen: true,
            isClosable: true,
            variant: getPaywallVariant(),
          });

          return;
        }

        // Check if free user has reached their limit using new context logic
        if (isFreePlan && !canAddToShoppingList(currentUser.plan)) {
          setPaywall({
            isOpen: true,
            isClosable: true,
            variant: getPaywallVariant(),
          });

          return;
        }
      } else {
        // Control: Only premium users can use shopping list
        if (!canUsePlanner) {
          setPaywall({
            isOpen: true,
            isClosable: true,
            variant: getPaywallVariant(),
          });

          return;
        }
      }

      if (isLoading) return;

      try {
        const eventData = {
          recipeAddedFrom,
          experimentName: "cro-web-147-shopping-list-to-free-users-paywall",
          experimentVariant: experimentVariant || "control",
          ...getEventDataFromRecipe(recipe),
        };

        // GTM Event with experiment data
        gtm.pushEvent("add_to_shopping_list", eventData);

        // PostHog Event (automatically includes feature flags and user properties)
        posthog.capture("add_to_shopping_list", {
          recipeAddedFrom,
          ...getEventDataFromRecipe(recipe),
        });

        setIsLoading(true);

        const success = await addRecipeToShoppingList(recipe.id);

        if (!success) {
          console.error("Failed to add recipe to shopping list");
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    },
    1000,
    { leading: true, trailing: false }
  );

  // ----------------------------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------------------------

  if (variant === "button") {
    return (
      <Button
        onClick={
          recipeInShoppingList
            ? () => setIsShoppingListOpen?.(true)
            : handleAddToShoppingList
        }
        size="lg"
        disabled={isLoading}
        className={clsx("space-x-1.5", className)}
        variant="outline"
      >
        {isLoading ? (
          <Spinner />
        ) : recipeInShoppingList ? (
          <CheckIcon className="size-5 stroke-[1.5]" />
        ) : (
          <AddToShoppingListIcon className="size-5 stroke-[1.5]" />
        )}
        <span>{recipeInShoppingList ? "View list" : "Add to plan"}</span>
      </Button>
    );
  }

  // Default: icon variant with tooltip (thumbnail usage)
  return (
    <div className="SaveRecipe">
      <TooltipPopover
        disabled={isLoading}
        onClick={
          recipeInShoppingList
            ? () => setIsShoppingListOpen?.(true)
            : handleAddToShoppingList
        }
        side="right"
        avoidCollisions
        hideWhenDetached={false}
      >
        <TooltipPopover.TooltipTrigger
          className={clsx(
            "w-component h-component flex shrink-0 items-center justify-center space-x-1.5 rounded-full bg-zinc-950 text-zinc-50 transition ease-out  hover:bg-white hover:text-zinc-950"
          )}
          label="Add to Shopping List"
        >
          {isLoading ? (
            <Spinner />
          ) : recipeInShoppingList ? (
            <CheckIcon className="size-5 stroke-[1.5]" />
          ) : (
            <AddToShoppingListIcon className="size-5 stroke-[1.5]" />
          )}
        </TooltipPopover.TooltipTrigger>
        <TooltipPopover.TooltipContent>
          <div className="TooltipContent">
            {recipeInShoppingList
              ? "View shopping list"
              : "Add to shopping list"}
          </div>
        </TooltipPopover.TooltipContent>
      </TooltipPopover>
    </div>
  );
};

export default observer(SaveRecipeToShoppingList);
